import { useState, useEffect, useCallback } from 'react'
import { adminService } from 'core/services/adminService'
import { apiClient } from 'core/http'
import { toast } from 'react-toastify'
import c from './assets/sass/leaderboard-admin.module.scss'

const REWARD_TYPES = [
    { value: 'COINS', label: 'Монеты' },
    { value: 'ENERGY', label: 'Энергия' },
    { value: 'CASE', label: 'Кейс (FREE)' },
    { value: 'PROMO_CODE', label: 'Промокод (пул)' },
    { value: 'PROMO_REUSABLE', label: 'Промокод (многоразовый)' },
    { value: 'CUSTOM', label: 'Кастомная награда' },
]

export const LeaderboardAdmin = () => {
    const [settings, setSettings] = useState({ coins_enabled: true, referrals_enabled: true })
    const [rewards, setRewards] = useState([])
    const [activeType, setActiveType] = useState('COINS')
    const [boards, setBoards] = useState([])
    const [saving, setSaving] = useState(false)
    const [promoTexts, setPromoTexts] = useState({})
    const [shopCases, setShopCases] = useState([])
    const [positionCount, setPositionCount] = useState({ COINS: 10, REFERRALS: 10 })
    const [blacklist, setBlacklist] = useState([])
    const [blacklistTgId, setBlacklistTgId] = useState('')
    const [blacklistReason, setBlacklistReason] = useState('')

    const loadData = useCallback(async () => {
        try {
            const [settingsRes, rewardsRes, boardsRes, casesRes, blRes] = await Promise.all([
                adminService.getLeaderboardSettings(),
                adminService.getLeaderboardRewards(),
                adminService.getLeaderboardCurrent(),
                adminService.getCasesList(),
                apiClient.get('/admin/leaderboard/blacklist'),
            ])
            if (settingsRes?.success) setSettings(settingsRes.settings)
            if (rewardsRes?.success) {
                const rw = rewardsRes.rewards || []
                setRewards(rw)
                const coinsMax = Math.max(10, ...rw.filter(r => r.type === 'COINS').map(r => r.position))
                const refsMax = Math.max(10, ...rw.filter(r => r.type === 'REFERRALS').map(r => r.position))
                setPositionCount(prev => ({ ...prev, COINS: coinsMax, REFERRALS: refsMax }))
            }
            if (boardsRes?.success) setBoards(boardsRes.boards || [])
            if (casesRes?.success) setShopCases(casesRes.cases || [])
            if (blRes?.data?.success) setBlacklist(blRes.data.blacklist || [])
        } catch {
            toast('Ошибка загрузки настроек', { type: 'error' })
        }
    }, [])

    useEffect(() => { loadData() }, [loadData])

    const handleToggle = async (field) => {
        const updated = { ...settings, [field]: !settings[field] }
        setSettings(updated)
        try {
            await adminService.updateLeaderboardSettings(updated)
            toast('Настройки сохранены', { type: 'success' })
        } catch {
            toast('Ошибка сохранения', { type: 'error' })
            setSettings(settings)
        }
    }

    const currentRewards = rewards.filter(r => r.type === activeType)

    const getRewardForPosition = (pos) => {
        return currentRewards.find(r => r.position === pos) || {
            type: activeType,
            position: pos,
            reward_type: 'COINS',
            reward_amount: '0',
        }
    }

    const updateReward = (pos, field, value) => {
        setRewards(prev => {
            const existing = prev.findIndex(r => r.type === activeType && r.position === pos)
            if (existing >= 0) {
                const updated = [...prev]
                updated[existing] = { ...updated[existing], [field]: value }
                return updated
            }
            return [...prev, { type: activeType, position: pos, reward_type: 'COINS', reward_amount: '0', [field]: value }]
        })
    }

    const handleSaveRewards = async () => {
        setSaving(true)
        try {
            const count = positionCount[activeType] || 10
            const toSave = []
            for (let pos = 1; pos <= count; pos++) {
                const r = getRewardForPosition(pos)
                toSave.push({
                    type: activeType,
                    position: pos,
                    reward_type: r.reward_type,
                    reward_amount: parseInt(r.reward_amount) || 0,
                    case_id: r.case_id || null,
                    promo_code: r.promo_code || null,
                    description: r.description || null,
                    image_url: r.image_url || null,
                })
            }
            await adminService.updateLeaderboardRewards(toSave)
            toast('Награды сохранены', { type: 'success' })
            loadData()
        } catch {
            toast('Ошибка сохранения наград', { type: 'error' })
        } finally {
            setSaving(false)
        }
    }

    const handleAddPosition = () => {
        setPositionCount(prev => ({ ...prev, [activeType]: (prev[activeType] || 10) + 1 }))
    }

    const handleRemovePosition = () => {
        setPositionCount(prev => {
            const current = prev[activeType] || 10
            if (current <= 1) return prev
            return { ...prev, [activeType]: current - 1 }
        })
    }

    const handleFinalize = async () => {
        if (!window.confirm('Вы уверены? Это финализирует текущие лидерборды и раздаст награды.')) return
        try {
            await adminService.finalizeLeaderboard()
            toast('Лидерборд финализирован', { type: 'success' })
            loadData()
        } catch {
            toast('Ошибка финализации', { type: 'error' })
        }
    }

    const handleAddPromos = async (pos) => {
        const text = promoTexts[pos]
        if (!text?.trim()) return
        const codes = text.split('\n').map(s => s.trim()).filter(Boolean)
        try {
            const res = await adminService.addLeaderboardPromoCodes(pos, activeType, codes)
            toast(`Добавлено ${res?.added || 0} промокодов`, { type: 'success' })
            setPromoTexts(prev => ({ ...prev, [pos]: '' }))
            loadData()
        } catch {
            toast('Ошибка добавления промокодов', { type: 'error' })
        }
    }

    return (
        <div className={c.component}>
            <div className={c.section}>
                <div className={c.sectionTitle}>Включение/выключение</div>
                <div className={c.toggleRow}>
                    <span>Лидерборд по монетам</span>
                    <div
                        className={`${c.toggle} ${settings.coins_enabled ? c.active : ''}`}
                        onClick={() => handleToggle('coins_enabled')}
                    />
                </div>
                <div className={c.toggleRow}>
                    <span>Лидерборд по рефералам</span>
                    <div
                        className={`${c.toggle} ${settings.referrals_enabled ? c.active : ''}`}
                        onClick={() => handleToggle('referrals_enabled')}
                    />
                </div>
            </div>

            <div className={c.section}>
                <div className={c.sectionTitle}>Текущие борды</div>
                {boards.length === 0 && <div style={{ color: '#888', fontSize: 13 }}>Нет активных бордов</div>}
                {boards.map(b => (
                    <div key={b.id} className={c.boardInfo}>
                        <div><strong>{b.type}</strong> — {b.status}</div>
                        <div>Начало: {new Date(b.startDate).toLocaleDateString('ru')}</div>
                        <div>Конец: {new Date(b.endDate).toLocaleDateString('ru')}</div>
                    </div>
                ))}
                <button className={c.dangerBtn} onClick={handleFinalize}>
                    Ручная финализация
                </button>
            </div>

            <div className={c.section}>
                <div className={c.sectionTitle}>Настройка наград</div>
                <div className={c.tabRow}>
                    <button
                        className={`${c.tabBtn} ${activeType === 'COINS' ? c.active : ''}`}
                        onClick={() => setActiveType('COINS')}
                    >
                        Монеты
                    </button>
                    <button
                        className={`${c.tabBtn} ${activeType === 'REFERRALS' ? c.active : ''}`}
                        onClick={() => setActiveType('REFERRALS')}
                    >
                        Рефералы
                    </button>
                </div>

                <div style={{ display: 'flex', gap: '8px', marginBottom: '12px', alignItems: 'center' }}>
                    <span style={{ color: '#ccc', fontSize: '13px' }}>Призовых мест: <strong style={{ color: '#EEAC2D' }}>{positionCount[activeType] || 10}</strong></span>
                    <button className={c.smallBtn} onClick={handleAddPosition} style={{ padding: '4px 12px' }}>+</button>
                    <button className={c.smallBtn} onClick={handleRemovePosition} style={{ padding: '4px 12px' }}>−</button>
                </div>

                {Array.from({ length: positionCount[activeType] || 10 }, (_, i) => i + 1).map(pos => {
                    const reward = getRewardForPosition(pos)
                    return (
                        <div key={pos}>
                            <div className={c.rewardRow}>
                                <div className={c.position}>#{pos}</div>
                                <select
                                    className={c.rewardType}
                                    value={reward.reward_type}
                                    onChange={e => updateReward(pos, 'reward_type', e.target.value)}
                                >
                                    {REWARD_TYPES.map(t => (
                                        <option key={t.value} value={t.value}>{t.label}</option>
                                    ))}
                                </select>
                                {['COINS', 'ENERGY'].includes(reward.reward_type) && (
                                    <input
                                        className={c.rewardInput}
                                        type="number"
                                        value={reward.reward_amount || ''}
                                        onChange={e => updateReward(pos, 'reward_amount', e.target.value)}
                                        placeholder="Кол-во"
                                    />
                                )}
                                {reward.reward_type === 'CASE' && (
                                    <select
                                        className={c.rewardInput}
                                        value={reward.case_id || ''}
                                        onChange={e => updateReward(pos, 'case_id', e.target.value)}
                                    >
                                        <option value="">— Кейс —</option>
                                        {shopCases.map(cs => (
                                            <option key={cs.id} value={cs.id}>
                                                {cs.name} ({parseInt(cs.price)} мон.)
                                            </option>
                                        ))}
                                    </select>
                                )}
                                {reward.reward_type === 'PROMO_REUSABLE' && (
                                    <input
                                        className={c.rewardInput}
                                        type="text"
                                        value={reward.promo_code || ''}
                                        onChange={e => updateReward(pos, 'promo_code', e.target.value)}
                                        placeholder="Промокод"
                                    />
                                )}
                                {reward.reward_type === 'CUSTOM' && (
                                    <input
                                        className={c.rewardInput}
                                        type="text"
                                        value={reward.description || ''}
                                        onChange={e => updateReward(pos, 'description', e.target.value)}
                                        placeholder="Описание"
                                    />
                                )}
                                {reward.reward_type === 'PROMO_CODE' && (
                                    <span style={{ fontSize: 11, color: '#888' }}>
                                        {reward.promoCodes ? `${reward.promoCodes.available}/${reward.promoCodes.total}` : '0/0'}
                                    </span>
                                )}
                            </div>
                            {reward.reward_type === 'PROMO_CODE' && (
                                <div className={c.promoSection}>
                                    <div className={c.promoInput}>
                                        <textarea
                                            value={promoTexts[pos] || ''}
                                            onChange={e => setPromoTexts(prev => ({ ...prev, [pos]: e.target.value }))}
                                            placeholder="Промокоды (по одному на строку)"
                                        />
                                        <button className={c.smallBtn} onClick={() => handleAddPromos(pos)}>
                                            Добавить
                                        </button>
                                    </div>
                                </div>
                            )}
                        </div>
                    )
                })}

                <button className={c.saveBtn} onClick={handleSaveRewards} disabled={saving}>
                    {saving ? 'Сохранение...' : 'Сохранить награды'}
                </button>
            </div>

            <div className={c.section}>
                <h3 className={c.sectionTitle}>Чёрный список лидерборда</h3>
                <p style={{ fontSize: 12, color: '#888', marginBottom: 12 }}>
                    Пользователи из этого списка не отображаются в лидербордах (монеты + рефералы)
                </p>
                <div style={{ display: 'flex', gap: 8, marginBottom: 12, flexWrap: 'wrap' }}>
                    <input
                        className={c.rewardInput}
                        type="text"
                        value={blacklistTgId}
                        onChange={e => setBlacklistTgId(e.target.value)}
                        placeholder="Telegram ID"
                        style={{ width: 160 }}
                    />
                    <input
                        className={c.rewardInput}
                        type="text"
                        value={blacklistReason}
                        onChange={e => setBlacklistReason(e.target.value)}
                        placeholder="Причина (бот, блогер...)"
                        style={{ flex: 1, minWidth: 140 }}
                    />
                    <button
                        className={c.smallBtn}
                        onClick={async () => {
                            if (!blacklistTgId.trim()) return
                            try {
                                await apiClient.post('/admin/leaderboard/blacklist', {
                                    telegramId: blacklistTgId.trim(),
                                    reason: blacklistReason.trim() || null,
                                })
                                toast('Добавлен в ЧС', { type: 'success' })
                                setBlacklistTgId('')
                                setBlacklistReason('')
                                loadData()
                            } catch (e) {
                                toast(e?.response?.data?.error || 'Ошибка', { type: 'error' })
                            }
                        }}
                    >
                        Добавить
                    </button>
                </div>
                {blacklist.length > 0 && (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                        {blacklist.map(item => (
                            <div key={item.id} style={{
                                display: 'flex', alignItems: 'center', gap: 8,
                                padding: '8px 12px', background: '#1a1a1a', borderRadius: 6, fontSize: 12
                            }}>
                                <span style={{ color: '#fff', fontWeight: 600 }}>
                                    {item.username || item.firstName || 'N/A'}
                                </span>
                                <span style={{ color: '#888' }}>TG: {item.telegramId}</span>
                                {item.reason && <span style={{ color: '#EEAC2D' }}>{item.reason}</span>}
                                <button
                                    style={{
                                        marginLeft: 'auto', background: '#5c2020', border: 'none',
                                        color: '#ff6b6b', padding: '4px 8px', borderRadius: 4, cursor: 'pointer', fontSize: 11
                                    }}
                                    onClick={async () => {
                                        try {
                                            await apiClient.delete(`/admin/leaderboard/blacklist/${item.id}`)
                                            toast('Удалён из ЧС', { type: 'success' })
                                            loadData()
                                        } catch { toast('Ошибка', { type: 'error' }) }
                                    }}
                                >
                                    Убрать
                                </button>
                            </div>
                        ))}
                    </div>
                )}
            </div>
        </div>
    )
}
